'use client';
/** @jsxImportSource @emotion/react */

import { type ElementType, forwardRef, type ReactNode, type Ref } from 'react';

import type {
  OverridableComponent,
  OverrideProps,
} from '@mui/material/OverridableComponent';

import MuiBreadcrumbs, {
  type BreadcrumbsTypeMap as MuiBreadcrumbsTypeMap,
} from '@mui/material/Breadcrumbs';

import { CoreIcon } from '../core-icon/CoreIcon';

type ExcludedMuiProps = 'separator'; // Remove optional

type CustomProps = {
  separator: ReactNode;
  component?: ElementType;
};

type BreadcrumbsTypeMap<
  P = CustomProps,
  D extends ElementType = MuiBreadcrumbsTypeMap['defaultComponent'],
> = {
  defaultComponent: D;
  props: Omit<MuiBreadcrumbsTypeMap['props'], ExcludedMuiProps> & P;
};

export type BreadcrumbsProps<
  D extends ElementType = MuiBreadcrumbsTypeMap['defaultComponent'],
  P = CustomProps,
> = OverrideProps<MuiBreadcrumbsTypeMap<P, D>, D>;

const BreadcrumbsComponent = (
  {
    separator = <CoreIcon name="chevronright" size="xs" />,
    sx,
    ...props
  }: BreadcrumbsProps,
  ref: Ref<HTMLElement>
) => {
  return (
    <MuiBreadcrumbs
      {...props}
      ref={ref}
      separator={separator}
      sx={[
        (theme) => ({
          /**
           * Apply border-radius to the collapsed button (the "..." button shown when maxItems is exceeded).
           * MUI Breadcrumbs only exposes a `CollapsedIcon` slot but not the button itself.
           * The collapsed button is an internal `BreadcrumbCollapsedButton` component (based on ButtonBase)
           * that cannot be styled via theme overrides, so we use a CSS selector here.
           */
          '& .MuiButtonBase-root': {
            borderRadius: `${theme.shape.borderRadius}px`,
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
};

BreadcrumbsComponent.displayName = 'Breadcrumbs';

export const Breadcrumbs = forwardRef(
  BreadcrumbsComponent
) as OverridableComponent<BreadcrumbsTypeMap>;
